import { z } from 'zod/v4';

/**
 * One FAQ/accordion entry as the AI tools accept it — `title`/`content` are the only fields a
 * model can legitimately author. `isOpen`/`contentHeight` are runtime toggle state the PUBLISHED
 * renderer owns (`apps/websites-builder/.../widgets/accordion/accordion.component.ts`'s `toggle()`)
 * — the fold seeds them (`false`/`'0px'`), matching what a freshly created item gets today.
 */
export const partialAccordionItemSchema = z.object({
    title: z.string().optional().describe('Question / header HTML for this entry, e.g. "<h2>...</h2>".'),
    content: z.string().optional().describe('Answer / body HTML shown when this entry is expanded.'),
});

export type PartialAccordionItem = z.infer<typeof partialAccordionItemSchema>;

/** Defaults a partial item is filled with for whatever the model omitted — never partially merged. */
export const ACCORDION_ITEM_DEFAULTS: Required<PartialAccordionItem> = {
    title: '<h2>Question</h2>',
    content: '<p>Answer</p>',
};

/**
 * THE accordion widget data shape as AI tools accept it — flat, all-optional, shared by
 * `create_accordion_widget`/`update_accordion_widget`. Field names match the entity 1:1 (no
 * renaming fold needed).
 *
 * `accordionList`, when provided, REPLACES the widget's list wholesale — same reasoning as
 * `navigation`/`menu`'s `menuList`: there is no id on an entry, so there is no stable key to merge
 * by. Omit it to leave the existing list untouched on update, or accept the 3-item default on
 * create.
 *
 * `position` is deliberately NOT exposed — same call already made for `navigation`/`menu`, whose
 * AI surface stops at content/appearance and leaves placement to the block/column tools.
 */
export const partialAccordionWidgetDataSchema = z.object({
    accordionList: z.array(partialAccordionItemSchema).optional().describe('The FAQ entries, top to bottom.'),
    horizontalAlign: z.enum(['L', 'R']).optional().describe('Which side the expand/collapse icon sits on.'),
    iconType: z.enum(['Caret', 'Plus']).optional().describe('The expand/collapse icon shape.'),
    isIndent: z.boolean().optional().describe('Whether a separator line is drawn between entries.'),
    indentColor: z.string().optional().describe('Separator line colour. Only visible when isIndent is true.'),
    iconColor: z.string().optional().describe('Expand/collapse icon colour.'),
    isAutoCollapse: z
        .boolean()
        .optional()
        .describe('When true, opening one entry closes every other entry. When false, entries open independently.'),
    isSynchronizeStyle: z
        .boolean()
        .optional()
        .describe('Whether tablet/mobile styling stays synchronized with the desktop styling.'),
    hideOnDesktop: z.boolean().optional().describe('Hide this widget on desktop screens.'),
    hideOnTablet: z.boolean().optional().describe('Hide this widget on tablet screens.'),
    hideOnMobile: z.boolean().optional().describe('Hide this widget on mobile screens.'),
});

export type PartialAccordionWidgetData = z.infer<typeof partialAccordionWidgetDataSchema>;
